/**
 * Module-level registry mapping widget id → currently-rendered data rows.
 *
 * GenericMetricWidget registers/unregisters its rows so WidgetCard's
 * WidgetExportMenu can read them for CSV export without prop-drilling
 * the data through React Grid Layout's wrapping divs. Re-renders inside
 * GenericMetricWidget cheaply replace the cached value.
 *
 * Phase J of issue #90 v3 plan.
 */

import { useSyncExternalStore } from 'react'

const ROWS = new Map<string, Array<Record<string, unknown>>>()
const SUBSCRIBERS = new Map<string, Set<() => void>>()

function notify(widgetId: string) {
  const subs = SUBSCRIBERS.get(widgetId)
  if (subs) for (const fn of subs) fn()
}

export function setWidgetExportRows(widgetId: string, rows: Array<Record<string, unknown>>) {
  ROWS.set(widgetId, rows)
  notify(widgetId)
}

export function clearWidgetExportRows(widgetId: string) {
  ROWS.delete(widgetId)
  notify(widgetId)
}

export function getWidgetExportRows(widgetId: string): Array<Record<string, unknown>> | undefined {
  return ROWS.get(widgetId)
}

/**
 * React hook — subscribes to the registry for a single widget id so the
 * caller re-renders when the data swaps. Used by WidgetCard to pull the
 * latest fiscal-year value for the GoalStatusChip without prop-drilling
 * through react-grid-layout's wrapping divs.
 */
export function useWidgetExportRows(widgetId: string) {
  const subscribe = (cb: () => void) => {
    let subs = SUBSCRIBERS.get(widgetId)
    if (!subs) {
      subs = new Set()
      SUBSCRIBERS.set(widgetId, subs)
    }
    subs.add(cb)
    return () => {
      subs?.delete(cb)
      if (subs && subs.size === 0) SUBSCRIBERS.delete(widgetId)
    }
  }
  const getSnapshot = () => ROWS.get(widgetId)
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
